import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Eye, MessageCircle, Ban, Search, Plus, CalendarDays, BarChart3 } from "lucide-react";

import {
    cancelLocalGame,
    createLocalGame,
    getLocalGameDetail,
    listLocalGameChatMessages,
    listLocalGames,
    toggleLocalGameChat,
    getLocalGameAnalytics,
    listLocalCustomVenues,
} from "./localGameApi";

import FormButton from "../../../components/form/FormButton";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import FormSelect from "../../../components/form/FormSelect";
import FormInput from "../../../components/form/FormInput";
import Paginator from "../../../components/table/Paginator";

import "./local-games.css";
import LocalGameDetailModal from "./LocalGameDetailModal";
import LocalGameChatModal from "./LocalGameChatModal";
import LocalGameForm from "./LocalGameForm";
import LocalGameAnalyticsPanel from "./LocalGameAnalyticsPanel";

const STATUS_OPTIONS = [
    { value: "all", label: "All Status" },
    { value: "upcoming", label: "Upcoming" },
    { value: "ongoing", label: "Ongoing" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
];

const DATE_PRESET_OPTIONS = [
    { value: "today", label: "TODAY'S GAMES", key: "today" },
    { value: "tomorrow", label: "TOMORROW'S GAMES", key: "tomorrow" },
    { value: "thisweek", label: "THIS WEEK", key: "thisWeek" },
    { value: "all", label: "TOTAL LOCAL GAMES", key: "total" },
];

const VENUE_TYPE_OPTIONS = [
    { value: "all", label: "All Venues" },
    { value: "ONGROUND", label: "OnGround" },
    { value: "CUSTOM", label: "Custom" },
];

const formatDate = (value) => {
    if (!value) return "—";

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";

    return d.toLocaleDateString();
};

const formatDateTime = (value) => {
    if (!value) return "—";

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";

    return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};

const statusClass = (status) => {
    const value = String(status || "").toLowerCase();

    if (value === "upcoming") return "lg-status upcoming";
    if (value === "ongoing") return "lg-status ongoing";
    if (value === "completed") return "lg-status completed";
    if (value === "cancelled") return "lg-status cancelled";

    return "lg-status";
};

const LocalGamesPage = () => {
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState({
        page: 1,
        limit: 20,
        totalPages: 1,
    });

    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailItem, setDetailItem] = useState(null);
    const [chatOpen, setChatOpen] = useState(false);
    const [chatLoading, setChatLoading] = useState(false);
    const [chatData, setChatData] = useState(null);
    const [cancelling, setCancelling] = useState(null);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [chatToggleTarget, setChatToggleTarget] = useState(null);
    const [chatToggleLoading, setChatToggleLoading] = useState(false);
    const [analyticsOpen, setAnalyticsOpen] = useState(false);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [analyticsFilter, setAnalyticsFilter] = useState("all");
    const [analyticsStatus, setAnalyticsStatus] = useState("all");

    const [customVenues, setCustomVenues] = useState([]);
    const [customVenuesLoading, setCustomVenuesLoading] = useState(false);
    const [customVenuesSearch, setCustomVenuesSearch] = useState("");
    const [debouncedCustomVenueSearch, setDebouncedCustomVenueSearch] = useState("");
    const [customVenuesMeta, setCustomVenuesMeta] = useState({
        page: 1,
        limit: 20,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
        total: 0,
    });

    const [filters, setFilters] = useState({
        status: "all",
        venueType: "all",
        datePreset: "all",
        search: "",
    });

    const [debouncedSearch, setDebouncedSearch] = useState("");

    const page = Number(meta.page || 1);
    const limit = Number(meta.limit || 20);
    const totalPages = Number(meta.totalPages || 1);
    const totalItems = Number(meta.total || 0);
    const showingFrom = totalItems === 0 ? 0 : (page - 1) * limit + 1;
    const showingTo = totalItems === 0 ? 0 : Math.min(page * limit, totalItems);
    const summary = meta.summary || {};

    const queryParams = useMemo(
        () => ({
            page,
            limit,
            status: filters.status || "all",
            ...(filters.datePreset && filters.datePreset !== "all"
                ? { datePreset: filters.datePreset }
                : {}),
            ...(filters.venueType && filters.venueType !== "all"
                ? { venueType: filters.venueType }
                : {}),
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
        }),
        [
            page,
            limit,
            filters.status,
            filters.venueType,
            filters.datePreset,
            debouncedSearch,
        ]
    );

    const load = async (override = {}) => {
        setLoading(true);

        try {
            const data = await listLocalGames({
                ...queryParams,
                ...override,
            });

            setItems(Array.isArray(data.items) ? data.items : []);
            setMeta({
                page: data?.meta?.page || 1,
                limit: data?.meta?.limit || 20,
                totalPages: data?.meta?.totalPages || 1,
                hasNextPage: data?.meta?.hasNextPage,
                hasPrevPage: data?.meta?.hasPrevPage,
                summary: data?.meta?.summary || {},
                filters: data?.meta?.filters || {},
                total: Number(data?.total || 0),
            });
        } catch (error) {
            toast.error(
                error?.response?.data?.message || "Failed to load local games"
            );
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        const timer = setTimeout(() => {
            const nextSearch = filters.search.trim();

            setDebouncedSearch((prev) =>
                prev === nextSearch ? prev : nextSearch
            );

            setMeta((prev) =>
                prev.page === 1
                    ? prev
                    : {
                        ...prev,
                        page: 1,
                    }
            );
        }, 400);

        return () => clearTimeout(timer);
    }, [filters.search]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, limit, filters.status, filters.venueType, filters.datePreset, debouncedSearch]);

    const updateFilter = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));

        if (key !== "search") {
            setMeta((prev) => ({
                ...prev,
                page: 1,
            }));
        }
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();

        const nextSearch = filters.search.trim();

        setDebouncedSearch(nextSearch);

        setMeta((prev) => ({
            ...prev,
            page: 1,
        }));
    };


    const handlePage = (nextPage) => {
        setMeta((prev) => ({
            ...prev,
            page: nextPage,
        }));
    };
    const fetchCustomVenues = async (
        pageValue = customVenuesMeta.page || 1,
        limitValue = customVenuesMeta.limit || 20
    ) => {
        setCustomVenuesLoading(true);

        try {
            const data = await listLocalCustomVenues({
                page: pageValue,
                limit: limitValue,
                ...(debouncedCustomVenueSearch
                    ? { search: debouncedCustomVenueSearch }
                    : {}),
            });

            setCustomVenues(Array.isArray(data.items) ? data.items : []);
            setCustomVenuesMeta({
                page: data?.meta?.page || pageValue,
                limit: data?.meta?.limit || limitValue,
                totalPages: data?.meta?.totalPages || 1,
                hasNextPage: data?.meta?.hasNextPage || false,
                hasPrevPage: data?.meta?.hasPrevPage || false,
                total: Number(data?.total || 0),
                search: data?.meta?.search || null,
            });
        } catch (error) {
            toast.error(
                error?.response?.data?.message || "Failed to load custom venues"
            );
            setCustomVenues([]);
        } finally {
            setCustomVenuesLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            const nextSearch = customVenuesSearch.trim();

            setDebouncedCustomVenueSearch((prev) =>
                prev === nextSearch ? prev : nextSearch
            );

            setCustomVenuesMeta((prev) =>
                prev.page === 1
                    ? prev
                    : {
                        ...prev,
                        page: 1,
                    }
            );
        }, 400);

        return () => clearTimeout(timer);
    }, [customVenuesSearch]);

    useEffect(() => {
        if (!analyticsOpen) return;

        fetchCustomVenues(
            customVenuesMeta.page || 1,
            customVenuesMeta.limit || 20
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        analyticsOpen,
        customVenuesMeta.page,
        customVenuesMeta.limit,
        debouncedCustomVenueSearch,
    ]);

    const handleCustomVenueSearchSubmit = (event) => {
        event.preventDefault();

        setDebouncedCustomVenueSearch(customVenuesSearch.trim());

        setCustomVenuesMeta((prev) => ({
            ...prev,
            page: 1,
        }));
    };

    const handleCustomVenuePage = (nextPage) => {
        setCustomVenuesMeta((prev) => ({
            ...prev,
            page: nextPage,
        }));
    };

    const handleCustomVenuePageSize = (nextLimit) => {
        setCustomVenuesMeta((prev) => ({
            ...prev,
            page: 1,
            limit: nextLimit,
        }));
    };
    const loadAnalytics = async (
        filterValue = analyticsFilter,
        statusValue = analyticsStatus
    ) => {
        setAnalyticsLoading(true);

        try {
            const data = await getLocalGameAnalytics({
                filter: filterValue,
                status: statusValue,
                limit: 10,
            });

            setAnalyticsData(data);
        } catch (error) {
            toast.error(
                error?.response?.data?.message || "Failed to load local game analytics"
            );
        } finally {
            setAnalyticsLoading(false);
        }
    };

    const handleAnalyticsFilterChange = async (nextFilter) => {
        setAnalyticsFilter(nextFilter);
        await loadAnalytics(nextFilter);
    };

    const handleAnalyticsStatusChange = async (nextStatus) => {
        setAnalyticsStatus(nextStatus);
        await loadAnalytics(analyticsFilter, nextStatus);
    };

    const handleOpenAnalytics = async () => {
        setAnalyticsOpen(true);

        if (!analyticsData) {
            await loadAnalytics();
        }

        await fetchCustomVenues(1, customVenuesMeta.limit || 20);
    };

    const handleCreateLocalGame = async (payload) => {
        try {
            await createLocalGame(payload);

            toast.success("Local game created");
            setCreating(false);

            await load({
                page: 1,
                limit,
            });
        } catch (error) {
            toast.error(
                error?.response?.data?.message || "Failed to create local game"
            );
            throw error;
        }
    };

    const handleOpenDetail = async (game) => {
        const id = game?._id || game?.id;

        if (!id) {
            toast.error("Missing local game id");
            return;
        }

        setDetailOpen(true);
        setDetailLoading(true);
        setDetailItem(null);

        try {
            const item = await getLocalGameDetail(id);
            setDetailItem(item);
        } catch (error) {
            toast.error(
                error?.response?.data?.message || "Failed to load local game detail"
            );
            setDetailOpen(false);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleOpenChat = async (game) => {
        const id = game?._id || game?.id;

        if (!id) {
            toast.error("Missing local game id");
            return;
        }

        setChatOpen(true);
        setChatLoading(true);
        setChatData(null);

        try {
            const data = await listLocalGameChatMessages(id, { limit: 50 });
            setChatData(data);
        } catch (error) {
            toast.error(
                error?.response?.data?.message || "Failed to load chat messages"
            );
            setChatOpen(false);
        } finally {
            setChatLoading(false);
        }
    };

    const handleCancelGame = async () => {
        const id = cancelling?._id || cancelling?.id;

        if (!id) {
            toast.error("Missing local game id");
            return;
        }

        try {
            setCancelLoading(true);

            await cancelLocalGame(id, {
                reason: "Cancelled by admin from admin panel",
            });

            toast.success("Local game cancelled");
            setCancelling(null);

            await load({
                page,
                limit,
            });
        } catch (error) {
            toast.error(
                error?.response?.data?.message || "Failed to cancel local game"
            );
        } finally {
            setCancelLoading(false);
        }
    };

    const handleToggleChat = async () => {
        const id = chatToggleTarget?._id || chatToggleTarget?.id;

        if (!id) {
            toast.error("Missing local game id");
            return;
        }

        const nextChatEnabled = !chatToggleTarget?.chatEnabled;

        try {
            setChatToggleLoading(true);

            await toggleLocalGameChat(id, {
                chatEnabled: nextChatEnabled,
                reason: nextChatEnabled
                    ? "Enabled by admin from admin panel"
                    : "Disabled by admin from admin panel",
            });

            toast.success(
                nextChatEnabled
                    ? "Local game chat enabled"
                    : "Local game chat disabled"
            );

            setChatToggleTarget(null);

            if (chatOpen) {
                setChatOpen(false);
                setChatData(null);
            }

            await load({
                page,
                limit,
            });
        } catch (error) {
            toast.error(
                error?.response?.data?.message || "Failed to update chat status"
            );
        } finally {
            setChatToggleLoading(false);
        }
    };

    const handlePageSize = (nextLimit) => {
        setMeta((prev) => ({
            ...prev,
            page: 1,
            limit: nextLimit,
        }));
    };

    return (
        <>
            <div className="sa-head-row local-games-head-row">
                <div>
                    <h1 className="sa-h1 sa-topbar-managed-page-title">Local Games</h1>
                    <p className="local-games-subtitle">
                        Review and moderate Find Players games created by users.
                    </p>
                </div>

                <div className="local-games-head-actions">
                    <FormButton
                        type="button"
                        variant="secondary"
                        className="local-games-analytics-btn"
                        onClick={handleOpenAnalytics}
                    >
                        <BarChart3 className="lucide" /> Analytics Dashboard
                    </FormButton>

                    <FormButton
                        type="button"
                        variant="primary"
                        className="local-games-new-btn"
                        onClick={() => setCreating(true)}
                    >
                        <Plus className="lucide" /> New Local Game
                    </FormButton>
                </div>
            </div>
            {analyticsOpen && (
                <LocalGameAnalyticsPanel
                    data={analyticsData}
                    loading={analyticsLoading}
                    onRefresh={() => loadAnalytics(analyticsFilter, analyticsStatus)}
                    onClose={() => setAnalyticsOpen(false)}
                    selectedFilter={analyticsFilter}
                    onFilterChange={handleAnalyticsFilterChange}
                    selectedStatus={analyticsStatus}
                    onStatusChange={handleAnalyticsStatusChange}
                    customVenues={customVenues}
                    customVenuesLoading={customVenuesLoading}
                    customVenuesMeta={customVenuesMeta}
                    customVenuesSearch={customVenuesSearch}
                    onCustomVenuesSearchChange={setCustomVenuesSearch}
                    onCustomVenueSearchSubmit={handleCustomVenueSearchSubmit}
                    onCustomVenueRefresh={() =>
                        fetchCustomVenues(
                            customVenuesMeta.page || 1,
                            customVenuesMeta.limit || 20
                        )
                    }
                    onCustomVenuePage={handleCustomVenuePage}
                    onCustomVenuePageSize={handleCustomVenuePageSize}
                />
            )}
            {!analyticsOpen && (
                <>

                    <div className="sa-card local-games-filter-card">
                        <div className="local-games-summary-grid">
                            {DATE_PRESET_OPTIONS.map((option) => {
                                const active = filters.datePreset === option.value;
                                const value = Number(summary?.[option.key] || 0);

                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`local-games-summary-card ${active ? "active" : ""}`}
                                        onClick={() => updateFilter("datePreset", option.value)}
                                    >
                                        <span className="local-games-summary-title">
                                            {option.label}
                                            <CalendarDays className="lucide" />
                                        </span>
                                        <strong>{value}</strong>
                                    </button>
                                );
                            })}
                        </div>
                        <form className="local-games-filters" onSubmit={handleSearchSubmit}>
                            <div className="local-games-search">
                                <Search className="lucide local-games-search-icon" />
                                <FormInput
                                    type="text"
                                    value={filters.search}
                                    onChange={(event) => updateFilter("search", event.target.value)}
                                    placeholder="Search by game code, game id, sport, or venue..."
                                />
                            </div>

                            <FormSelect
                                value={filters.status}
                                onChange={(event) => updateFilter("status", event.target.value)}
                            >
                                {STATUS_OPTIONS.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </FormSelect>

                            <FormSelect
                                value={filters.venueType}
                                onChange={(event) => updateFilter("venueType", event.target.value)}
                            >
                                {VENUE_TYPE_OPTIONS.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </FormSelect>

                        </form>
                    </div>

                    <div className="sa-card">
                        {loading ? (
                            <div className="sa-placeholder" style={{ height: 140 }}>
                                Loading local games…
                            </div>
                        ) : items.length === 0 ? (
                            <div className="sa-empty">
                                <div>No local games found.</div>
                            </div>
                        ) : (
                            <div className="sa-table-wrap local-games-table-wrap">
                                <table className="sa-table local-games-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 70 }}>S.N.</th>
                                            <th style={{ minWidth: 180 }}>Game</th>
                                            <th style={{ minWidth: 160 }}>Creator</th>
                                            <th style={{ minWidth: 180 }}>Venue</th>
                                            <th style={{ minWidth: 130 }}>Date / Time</th>
                                            <th style={{ minWidth: 150 }}>Created At</th>
                                            <th style={{ width: 120 }}>Players</th>
                                            <th style={{ width: 120 }}>Status</th>
                                            <th style={{ width: 110 }}>Chat</th>
                                            <th style={{ textAlign: "right", width: 130 }}>Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {items.map((game, index) => {
                                            const id = game._id || game.id;

                                            return (
                                                <tr key={id}>
                                                    <td>{(page - 1) * limit + index + 1}</td>

                                                    <td>
                                                        <div className="lg-main-cell">
                                                            <strong>{game.sportName || "—"}</strong>
                                                            <small>{game.gameCode || id}</small>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="lg-main-cell">
                                                            <strong>{game.creator?.fullName || "—"}</strong>
                                                            <small>{game.creator?.phoneE164 || game.creator?.email || "—"}</small>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="lg-main-cell">
                                                            <strong>{game.venueName || "—"}</strong>
                                                            <small>
                                                                {game.venueType || "—"}
                                                                {game.address ? ` • ${game.address}` : ""}
                                                            </small>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="lg-main-cell">
                                                            <strong>{game.date || formatDate(game.startAt)}</strong>
                                                            <small>
                                                                {game.startTime || "—"} - {game.endTime || "—"}
                                                            </small>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="lg-main-cell">
                                                            <strong>{formatDateTime(game.createdAt)}</strong>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <strong>
                                                            {game.joinedPlayers || 0}/{game.totalPlayers || 0}
                                                        </strong>
                                                        <small className="lg-muted">
                                                            {game.remainingPlayers || 0} left
                                                        </small>
                                                    </td>

                                                    <td>
                                                        <span className={statusClass(game.status)}>
                                                            {game.status || "—"}
                                                        </span>
                                                    </td>

                                                    <td>
                                                        <button
                                                            type="button"
                                                            className={
                                                                game.chatEnabled
                                                                    ? "lg-chat-pill enabled lg-chat-toggle-btn"
                                                                    : "lg-chat-pill disabled lg-chat-toggle-btn"
                                                            }
                                                            onClick={() => setChatToggleTarget(game)}
                                                            disabled={chatToggleLoading}
                                                            title={game.chatEnabled ? "Disable chat" : "Enable chat"}
                                                        >
                                                            {game.chatEnabled ? "Enabled" : "Disabled"}
                                                        </button>
                                                    </td>

                                                    <td className="sa-actions-cell">
                                                        <div className="sa-actions">
                                                            <button
                                                                type="button"
                                                                className="sa-icon-link"
                                                                title="View details"
                                                                aria-label="View details"
                                                                onClick={() => handleOpenDetail(game)}
                                                            >
                                                                <Eye className="lucide" />
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="sa-icon-link"
                                                                title="View chat"
                                                                aria-label="View chat"
                                                                onClick={() => handleOpenChat(game)}
                                                            >
                                                                <MessageCircle className="lucide" />
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="sa-icon-link danger"
                                                                title="Cancel game"
                                                                aria-label="Cancel game"
                                                                disabled={game.status === "cancelled" || cancelLoading}
                                                                onClick={() => setCancelling(game)}
                                                            >
                                                                <Ban className="lucide" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                <div className="local-games-table-footer">
                                    <div className="local-games-result-count">
                                        Showing {showingFrom} to {showingTo} of {totalItems} results
                                    </div>

                                    <Paginator
                                        page={page}
                                        totalPages={totalPages}
                                        onPage={handlePage}
                                        pageSize={limit}
                                        onPageSizeChange={handlePageSize}
                                        pageSizeOptions={[10, 20, 50, 100]}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            <LocalGameForm
                open={creating}
                onSubmit={handleCreateLocalGame}
                onCancel={() => setCreating(false)}
            />
            <LocalGameDetailModal
                open={detailOpen}
                item={detailItem}
                loading={detailLoading}
                onClose={() => {
                    if (detailLoading) return;
                    setDetailOpen(false);
                    setDetailItem(null);
                }}
            />
            <LocalGameChatModal
                open={chatOpen}
                data={chatData}
                loading={chatLoading}
                toggling={chatToggleLoading}
                onToggleChat={(target) => setChatToggleTarget(target)}
                onClose={() => {
                    if (chatLoading || chatToggleLoading) return;
                    setChatOpen(false);
                    setChatData(null);
                }}
            />
            <ConfirmDialog
                open={!!cancelling}
                title="Cancel Local Game"
                message={`Are you sure you want to cancel "${cancelling?.gameCode || cancelling?.sportName || "this local game"
                    }"? This will make the game cancelled and the chat read-only.`}
                confirmText="Cancel Game"
                cancelText="Keep Game"
                confirmVariant="danger"
                loading={cancelLoading}
                onConfirm={handleCancelGame}
                onCancel={() => {
                    if (cancelLoading) return;
                    setCancelling(null);
                }}
            />
            <ConfirmDialog
                open={!!chatToggleTarget}
                title={chatToggleTarget?.chatEnabled ? "Disable Chat" : "Enable Chat"}
                message={
                    chatToggleTarget?.chatEnabled
                        ? `Are you sure you want to disable chat for "${chatToggleTarget?.gameCode || chatToggleTarget?.sportName || "this local game"
                        }"? Players will still see old messages, but sending will be blocked.`
                        : `Are you sure you want to enable chat for "${chatToggleTarget?.gameCode || chatToggleTarget?.sportName || "this local game"
                        }"? Players will be able to send messages again if the game is active.`
                }
                confirmText={chatToggleTarget?.chatEnabled ? "Disable Chat" : "Enable Chat"}
                cancelText="Cancel"
                confirmVariant={chatToggleTarget?.chatEnabled ? "danger" : "primary"}
                loading={chatToggleLoading}
                onConfirm={handleToggleChat}
                onCancel={() => {
                    if (chatToggleLoading) return;
                    setChatToggleTarget(null);
                }}
            />
        </>
    );
};

export default LocalGamesPage;